/**
 * Cliente do click-to-call usado pelo widget (browser).
 *
 * - `pollClickToCall`: informa a presença do operador e recebe a próxima tarefa.
 * - `ackClickToCall`: confirma "calling" (makeManualCall disparado) ou "failed".
 * - `createBackgroundTicker`: temporizador que continua disparando com a aba em
 *   segundo plano. Timers da página são reduzidos a 1 por minuto pelo Chrome após
 *   alguns minutos em segundo plano; um Web Worker não sofre essa limitação. O
 *   usuário costuma clicar em "ligar" no CRM em OUTRA aba, então isso importa.
 */
import type { ClickToCallPollResponse, ClickToCallTaskPublic } from './clickToCallTypes';

export interface ClickToCallClientConfig {
  apiBaseUrl: string;
  headers: Record<string, string>;
}

export interface PollOptions extends ClickToCallClientConfig {
  state: string | undefined;
  claim: boolean;
  signal?: AbortSignal;
}

export interface AckOptions extends ClickToCallClientConfig {
  taskId: string;
  status: 'calling' | 'failed';
  error?: string;
  retry?: boolean;
}

/** Resultado do polling: `ok: false` traz o HTTP status e o código de erro da API (para o backoff). */
export type PollResult = ({ ok: true } & ClickToCallPollResponse) | { ok: false; status: number; code: string | null };

const REQUEST_TIMEOUT_MS = 10000;
const DEFAULT_PRESENCE_TTL_SECONDS = 45;

function withTimeout(signal?: AbortSignal): { signal: AbortSignal; clear: () => void } {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  if (signal) {
    if (signal.aborted) {
      controller.abort();
    } else {
      signal.addEventListener('abort', () => controller.abort(), { once: true });
    }
  }
  return { signal: controller.signal, clear: () => clearTimeout(timer) };
}

export async function pollClickToCall(options: PollOptions): Promise<PollResult> {
  const { signal, clear } = withTimeout(options.signal);
  try {
    const response = await fetch(`${options.apiBaseUrl}/api/click-to-call/poll`, {
      method: 'POST',
      headers: options.headers,
      body: JSON.stringify({ state: options.state ?? null, claim: options.claim }),
      credentials: 'omit',
      signal,
    });
    if (!response.ok) {
      let code: string | null = null;
      try {
        const data = (await response.json()) as { error?: unknown };
        code = typeof data?.error === 'string' ? data.error : null;
      } catch {
        // corpo não é JSON
      }
      return { ok: false, status: response.status, code };
    }
    const data = (await response.json()) as Partial<ClickToCallPollResponse>;
    return {
      ok: true,
      presence: data.presence ?? 'unknown',
      task: data.task && typeof data.task === 'object' && typeof data.task.phone === 'string' ? data.task : null,
      pollIntervalMs: typeof data.pollIntervalMs === 'number' ? data.pollIntervalMs : 0,
      presenceTtlSeconds: typeof data.presenceTtlSeconds === 'number' && data.presenceTtlSeconds > 0 ? data.presenceTtlSeconds : DEFAULT_PRESENCE_TTL_SECONDS,
    };
  } finally {
    clear();
  }
}

export type AckResult = { ok: true; task: ClickToCallTaskPublic | null } | { ok: false; status: number; code: string | null };

/** Confirma a tarefa. Lança em falha de rede/timeout; devolve ok:false em HTTP não-2xx. */
export async function ackClickToCall(options: AckOptions): Promise<AckResult> {
  const { signal, clear } = withTimeout();
  try {
    const response = await fetch(`${options.apiBaseUrl}/api/click-to-call/${encodeURIComponent(options.taskId)}`, {
      method: 'PATCH',
      headers: options.headers,
      body: JSON.stringify({ status: options.status, error: options.error, retry: options.retry === true }),
      credentials: 'omit',
      keepalive: true,
      signal,
    });
    if (!response.ok) {
      let code: string | null = null;
      try {
        const data = (await response.json()) as { error?: unknown };
        code = typeof data?.error === 'string' ? data.error : null;
      } catch {
        // corpo não é JSON
      }
      return { ok: false, status: response.status, code };
    }
    const data = (await response.json()) as { task?: ClickToCallTaskPublic };
    return { ok: true, task: data.task ?? null };
  } finally {
    clear();
  }
}

/** Avisa que o discador saiu (logout/fechou): a presença passa a "offline" na hora. */
export function reportClickToCallOffline(config: ClickToCallClientConfig): void {
  try {
    void fetch(`${config.apiBaseUrl}/api/click-to-call/poll`, {
      method: 'POST',
      headers: config.headers,
      body: JSON.stringify({ state: 'offline', claim: false }),
      credentials: 'omit',
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // fetch indisponível
  }
}

export interface BackgroundTicker {
  setInterval(ms: number): void;
  stop(): void;
}

const WORKER_SOURCE = `
let timer = null;
self.onmessage = (event) => {
  const interval = Number(event && event.data && event.data.interval) || 0;
  if (timer) { clearInterval(timer); timer = null; }
  if (interval > 0) { timer = setInterval(() => self.postMessage('tick'), interval); }
};
`;

/**
 * Dispara `callback` a cada `intervalMs`, preferindo um Web Worker (imune ao
 * throttling de abas em segundo plano). Cai para setInterval quando Worker/Blob
 * não estão disponíveis ou a CSP do host bloqueia workers de blob.
 */
export function createBackgroundTicker(callback: () => void, intervalMs: number): BackgroundTicker {
  let current = Math.max(250, Math.round(intervalMs));
  let fallbackTimer: ReturnType<typeof setInterval> | null = null;
  let worker: Worker | null = null;
  let objectUrl: string | null = null;
  let stopped = false;

  const startFallback = () => {
    if (stopped) return;
    if (fallbackTimer) clearInterval(fallbackTimer);
    fallbackTimer = setInterval(callback, current);
  };

  try {
    if (typeof Worker !== 'undefined' && typeof Blob !== 'undefined' && typeof URL !== 'undefined' && URL.createObjectURL) {
      objectUrl = URL.createObjectURL(new Blob([WORKER_SOURCE], { type: 'application/javascript' }));
      worker = new Worker(objectUrl);
      worker.onmessage = () => {
        if (!stopped) callback();
      };
      worker.onerror = () => {
        // CSP bloqueou o worker (ou falhou): usa o timer da página
        worker?.terminate();
        worker = null;
        startFallback();
      };
      worker.postMessage({ interval: current });
    } else {
      startFallback();
    }
  } catch {
    worker = null;
    startFallback();
  }

  return {
    setInterval(ms: number) {
      if (stopped) return;
      const next = Math.max(250, Math.round(ms));
      if (next === current) return;
      current = next;
      if (worker) {
        worker.postMessage({ interval: current });
      } else {
        startFallback();
      }
    },
    stop() {
      stopped = true;
      if (worker) {
        worker.terminate();
        worker = null;
      }
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
      if (fallbackTimer) {
        clearInterval(fallbackTimer);
        fallbackTimer = null;
      }
    },
  };
}

/** "+555132488509" -> "5132488509" (o que o visor do discador mostra); outros países ficam com o "+" e o DDI. */
export function nationalDigitsFromE164(e164: string): string {
  const digits = (e164 ?? '').replace(/\D/g, '');
  if (digits.startsWith('55') && (digits.length === 12 || digits.length === 13)) {
    return digits.slice(2);
  }
  return e164.startsWith('+') ? `+${digits}` : digits;
}
